<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
    style="position: relative"
  >
    <i
      v-if="loading || icon"
      :class="[
        loading ? 'pi pi-spinner tw-animate-spin' : icon ? icon : '',
        iconClass,
      ]"
    ></i>
    <span v-if="label">{{ label }}</span>
    <slot />
    <span
      v-if="badge"
      :class="[
        'tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-right-2 tw-min-w-[1.25rem] tw-h-[1.25rem] tw-px-1 tw-rounded-full tw-text-xs tw-flex tw-items-center tw-justify-center tw-font-bold tw-border tw-border-white dark:tw-border-gray-900',
        badgeClasses[badgeSeverity] || badgeClasses.danger,
      ]"
      style="pointer-events: none"
    >
      {{ badge }}
    </span>
  </button>
</template>

<script setup>
import { computed } from "vue";
import { toRefs } from "vue";

const emit = defineEmits(["click"]);

const props = defineProps({
  round: { type: Boolean, default: false },
  severity: {
    type: String,
    default: "primary", // primary, secondary, success, danger, warning, info, etc.
    validator: (v) =>
      [
        "primary",
        "secondary",
        "success",
        "danger",
        "warning",
        "info",
        "neutral",
      ].includes(v),
  },
  icon: { type: String, default: "" }, // e.g. 'pi pi-check'
  iconClass: { type: [String, Array, Object], default: "" }, // additional classes for icon
  disabled: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  size: {
    type: String,
    default: "md", // sm, md, lg
    validator: (v) => ["sm", "md", "lg"].includes(v),
  },
  outlined: { type: Boolean, default: false },
  text: { type: Boolean, default: false },
  class: { type: [String, Array, Object], default: "" },
  align: {
    type: String,
    default: "center",
    validator: (v) => ["left", "center", "right"].includes(v),
  },
  label: { type: String, default: "" },
  badge: { type: String, default: "" },
  badgeSeverity: {
    type: String,
    default: "danger",
    validator: (v) =>
      [
        "primary",
        "secondary",
        "success",
        "danger",
        "warning",
        "info",
        "neutral",
      ].includes(v),
  },
});

const {
  round,
  severity,
  disabled,
  loading,
  size,
  outlined,
  text,
  class: userClass,
  align,
  iconClass,
  badge,
  badgeSeverity,
} = toRefs(props);

const severityClasses = {
  primary:
    // violet-100: #EDE9FE, green-900: #14532D
    "tw-bg-violet-100 hover:tw-bg-violet-200 active:tw-bg-violet-300 tw-text-slate-700 dark:tw-bg-green-900 dark:hover:tw-bg-green-900 dark:active:tw-bg-green-800 dark:tw-text-slate-50",
  secondary:
    "tw-bg-gray-600 hover:tw-bg-gray-700 active:tw-bg-gray-800 tw-text-white dark:tw-bg-gray-700 dark:hover:tw-bg-gray-600 dark:active:tw-bg-gray-500",
  success:
    "tw-bg-green-600 hover:tw-bg-green-700 active:tw-bg-green-800 tw-text-white dark:tw-bg-green-700 dark:hover:tw-bg-green-600 dark:active:tw-bg-green-500",
  danger:
    "tw-bg-red-600 hover:tw-bg-red-700 active:tw-bg-red-800 tw-text-white dark:tw-bg-red-700 dark:hover:tw-bg-red-600 dark:active:tw-bg-red-500",
  warning:
    "tw-bg-yellow-500 hover:tw-bg-yellow-600 active:tw-bg-yellow-700 tw-text-white dark:tw-bg-yellow-600 dark:hover:tw-bg-yellow-500 dark:active:tw-bg-yellow-400",
  info: "tw-bg-cyan-600 hover:tw-bg-cyan-700 active:tw-bg-cyan-800 tw-text-white dark:tw-bg-cyan-700 dark:hover:tw-bg-cyan-600 dark:active:tw-bg-cyan-500",
  neutral:
    "tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 tw-text-slate-700 dark:tw-bg-gray-800 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 dark:tw-text-slate-50",
};

const outlinedClasses = {
  primary:
    // violet-100: #EDE9FE, green-900: #14532D
    "tw-border-violet-100 tw-text-violet-500 tw-bg-transparent hover:tw-bg-violet-100 active:tw-bg-violet-300 dark:tw-border-green-900 dark:tw-text-violet-100 dark:hover:tw-bg-green-900 dark:active:tw-bg-green-800",
  secondary:
    "tw-border-gray-600 tw-text-gray-600 tw-bg-transparent hover:tw-bg-gray-50 active:tw-bg-gray-100 dark:tw-border-gray-400 dark:tw-text-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600",
  success:
    "tw-border-green-600 tw-text-green-600 tw-bg-transparent hover:tw-bg-green-50 active:tw-bg-green-100 dark:tw-border-green-400 dark:tw-text-green-300 dark:hover:tw-bg-green-900 dark:active:tw-bg-green-800",
  danger:
    "tw-border-red-600 tw-text-red-600 tw-bg-transparent hover:tw-bg-red-50 active:tw-bg-red-100 dark:tw-border-red-400 dark:tw-text-red-300 dark:hover:tw-bg-red-900 dark:active:tw-bg-red-800",
  warning:
    "tw-border-yellow-500 tw-text-yellow-600 tw-bg-transparent hover:tw-bg-yellow-50 active:tw-bg-yellow-100 dark:tw-border-yellow-400 dark:tw-text-yellow-300 dark:hover:tw-bg-yellow-900 dark:active:tw-bg-yellow-800",
  info: "tw-border-cyan-600 tw-text-cyan-600 tw-bg-transparent hover:tw-bg-cyan-50 active:tw-bg-cyan-100 dark:tw-border-cyan-400 dark:tw-text-cyan-300 dark:hover:tw-bg-cyan-900 dark:active:tw-bg-cyan-800",
  neutral:
    "tw-border-gray-300 tw-text-slate-700 tw-bg-transparent hover:tw-bg-gray-100 active:tw-bg-gray-200 dark:tw-border-gray-700 dark:tw-text-slate-50 dark:hover:tw-bg-gray-800 dark:active:tw-bg-gray-700",
};

const textClasses = {
  primary:
    // violet-100: #EDE9FE, green-900: #14532D
    "tw-text-violet-500 hover:tw-bg-violet-100 active:tw-bg-violet-300 dark:tw-text-violet-100 dark:hover:tw-bg-green-900 dark:active:tw-bg-green-800",
  secondary:
    "tw-text-gray-600 hover:tw-bg-gray-50 active:tw-bg-gray-100 dark:tw-text-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600",
  success:
    "tw-text-green-600 hover:tw-bg-green-50 active:tw-bg-green-100 dark:tw-text-green-300 dark:hover:tw-bg-green-900 dark:active:tw-bg-green-800",
  danger:
    "tw-text-red-600 hover:tw-bg-red-50 active:tw-bg-red-100 dark:tw-text-red-300 dark:hover:tw-bg-red-900 dark:active:tw-bg-red-800",
  warning:
    "tw-text-yellow-600 hover:tw-bg-yellow-50 active:tw-bg-yellow-100 dark:tw-text-yellow-300 dark:hover:tw-bg-yellow-900 dark:active:tw-bg-yellow-800",
  info: "tw-text-cyan-600 hover:tw-bg-cyan-50 active:tw-bg-cyan-100 dark:tw-text-cyan-300 dark:hover:tw-bg-cyan-900 dark:active:tw-bg-cyan-800",
  neutral:
    "tw-text-slate-700 hover:tw-bg-gray-100 active:tw-bg-gray-200 dark:tw-text-slate-50 dark:hover:tw-bg-gray-800 dark:active:tw-bg-gray-700",
};

const sizeClasses = {
  sm: "tw-px-2 tw-h-8 tw-min-w-8 tw-text-sm tw-leading-4",
  md: "tw-px-2 tw-h-10 tw-min-w-10 tw-text-sm tw-leading-4",
  lg: "tw-px-4 tw-h-12 tw-min-w-12 tw-text-base tw-leading-5",
};

const alignClasses = {
  left: "tw-justify-start tw-text-left",
  center: "tw-justify-center tw-text-center",
  right: "tw-justify-end tw-text-right",
};

const badgeClasses = {
  primary: "tw-bg-violet-500 tw-text-white",
  secondary: "tw-bg-gray-500 tw-text-white",
  success: "tw-bg-green-600 tw-text-white",
  danger: "tw-bg-red-600 tw-text-white",
  warning: "tw-bg-yellow-500 tw-text-white",
  info: "tw-bg-cyan-600 tw-text-white",
  neutral: "tw-bg-gray-400 tw-text-white",
};

const buttonClasses = computed(() => [
  "tw-border tw-font-medium tw-inline-flex tw-items-center tw-font-medium tw-focus:outline-none tw-transition-all tw-duration-150 tw-gap-2 tw-overflow-hidden",
  round.value ? "tw-rounded-full" : "tw-rounded-lg",
  disabled.value || loading.value
    ? "tw-opacity-60 tw-cursor-not-allowed"
    : "tw-cursor-pointer",
  text.value
    ? "tw-border-transparent " + textClasses[severity.value]
    : outlined.value
    ? outlinedClasses[severity.value]
    : "tw-border-transparent " + severityClasses[severity.value],
  sizeClasses[size.value],
  userClass.value,
  alignClasses[align.value],
]);
</script>
